import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Loader2, Send, CheckCircle2 } from "lucide-react";
import { fmtMoney, scoreColor } from "@/lib/prospects";
import { toast } from "sonner";

const ALL = "__all__";

// Shared 3-field territory request form (used in public onboarding + logged-in Partner Portal).
export const TerritoryRequestForm = ({ onSubmit, busy, done, idPrefix = "territory" }) => {
  const [bikePath, setBikePath] = useState("");
  const [region, setRegion] = useState("");
  const [reason, setReason] = useState("");
  const valid = bikePath.trim() && region.trim() && reason.trim();

  const submit = () => onSubmit({ type: "area", bike_path: bikePath, region: region, reason: reason }, () => {
    setBikePath(""); setRegion(""); setReason("");
  });

  if (done) {
    return (
      <p className="text-xs text-emerald-400 flex items-center gap-1" data-testid={`${idPrefix}-done`}>
        <CheckCircle2 className="w-3.5 h-3.5" />Request received — we'll follow up by email.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid={`${idPrefix}-form`}>
      <div className="space-y-1.5">
        <Label className="text-xs">Bike Path / Trail System</Label>
        <Input value={bikePath} onChange={(e) => setBikePath(e.target.value)} placeholder="e.g. Pine Creek Rail Trail" data-testid={`${idPrefix}-bikepath`} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Region / Location</Label>
        <Input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="e.g. Wellsboro, PA" data-testid={`${idPrefix}-region`} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Why are you interested in managing this territory?</Label>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Tell us about your local knowledge and why you'd be a great fit for this territory." data-testid={`${idPrefix}-reason`} />
      </div>
      <Button size="sm" disabled={busy || !valid} onClick={submit} data-testid={`${idPrefix}-submit`}>
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Send request
      </Button>
    </div>
  );
};

// shopId/email used for public onboarding; authed=true for logged-in shops.
export const ZoneDiscovery = ({ shopId, email, authed = false }) => {
  const [data, setData] = useState({ items: [], states: [] });
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(ALL);
  const [busy, setBusy] = useState(false);
  const [requested, setRequested] = useState({});
  const [areaDone, setAreaDone] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (shopId) params.shop_id = shopId;
    if (state !== ALL) params.state = state;
    api.get("/public/available-zones", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [shopId, state]);

  const submit = async (payload, onOk) => {
    setBusy(true);
    try {
      const url = authed ? "/shop/zone-requests" : "/public/zone-requests";
      await api.post(url, authed ? payload : { ...payload, shop_id: shopId, email });
      onOk();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const requestZone = (z) => submit({ type: "zone", zone_id: z.zoneId }, () => {
    setRequested((p) => ({ ...p, [z.zoneId]: true }));
    toast.success(`Requested ${z.zoneName} — VeloPaths will follow up.`);
  });
  const requestArea = (payload, reset) => submit(payload, () => {
    setAreaDone(true); reset(); toast.success("Request sent — we'll be in touch about matching zones.");
  });

  const zones = data.items.slice(0, 8);

  return (
    <div className="space-y-4" data-testid="zone-discovery">
      <div>
        <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /><h4 className="font-heading font-bold text-sm">Available zones {shopId ? "near you" : ""}</h4></div>
        <p className="text-xs text-muted-foreground">Preview territories you could lease. Zones are assigned by VeloPaths Admin after approval — request the ones you want below.</p>
      </div>

      <Select value={state} onValueChange={setState}>
        <SelectTrigger className="w-full h-9" data-testid="discovery-state"><SelectValue placeholder="Filter by state" /></SelectTrigger>
        <SelectContent><SelectItem value={ALL}>All states</SelectItem>{data.states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>

      {loading ? <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        : zones.length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center" data-testid="discovery-empty">No available zones match right now — use the request box below and we'll notify you.</p>
        : (
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="discovery-list">
            {zones.map((z) => (
              <div key={z.zoneId} className="rounded-xl border border-border bg-secondary/40 p-3 flex items-center gap-3" data-testid={`discovery-zone-${z.zoneId}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{z.zoneName} {z.humanId && <span className="text-xs text-muted-foreground">({z.humanId})</span>}</div>
                  <div className="text-xs text-muted-foreground">{z.state || "—"} · {z.classification} · <span className="text-foreground">${Number(z.annualFee).toFixed(0)}/yr</span>{z.onPromo && <Badge className="ml-1 bg-rose-500/15 text-rose-300 border border-rose-500/30 text-[10px]">promo</Badge>}{z.distanceMiles != null && <span> · {z.distanceMiles} mi away</span>}</div>
                  <div className="mt-1 flex items-center gap-2 text-[11px]" data-testid={`discovery-metrics-${z.zoneId}`}>
                    <span className="text-primary font-semibold">{z.prospectCount ?? 0} prospects</span>
                    <span className={`font-semibold ${scoreColor(z.opportunityScore ?? 0)}`}>Opp {z.opportunityScore ?? 0}</span>
                    <span className="text-emerald-400 font-semibold">{fmtMoney(z.projectedMonthly)}/mo</span>
                  </div>
                </div>
                {requested[z.zoneId]
                  ? <span className="text-xs text-emerald-400 flex items-center gap-1" data-testid={`discovery-requested-${z.zoneId}`}><CheckCircle2 className="w-3.5 h-3.5" />Requested</span>
                  : <Button size="sm" variant="outline" disabled={busy} onClick={() => requestZone(z)} data-testid={`discovery-request-${z.zoneId}`}>Request</Button>}
              </div>
            ))}
          </div>
        )}

      <div className="rounded-xl border border-border p-3 space-y-2" data-testid="discovery-area-request">
        <p className="text-xs font-medium">Don't see your area? Tell us about the territory you'd like to manage</p>
        <TerritoryRequestForm onSubmit={requestArea} busy={busy} done={areaDone} idPrefix="discovery-area" />
      </div>
    </div>
  );
};

// Territory-wishlist box for logged-in partners (used in the Marketplace).
export const ShopAreaRequest = () => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const send = async (payload, reset) => {
    setBusy(true);
    try { await api.post("/shop/zone-requests", payload); setDone(true); reset(); toast.success("Territory request sent to VeloPaths."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card p-4 space-y-3" data-testid="shop-area-request">
      <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary shrink-0" /><span className="text-sm font-medium">Can't find your territory? Tell us about it</span></div>
      <TerritoryRequestForm onSubmit={send} busy={busy} done={done} idPrefix="shop-area-request" />
    </div>
  );
};
